import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useNotifications,
  useUpdateNotification,
  useDeleteNotification,
  useMarkAllRead,
  NotificationType,
  getNotificationTypeColor,
  type Notification,
} from '@/features/notifications/api/useNotifications'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { formatDistanceToNow } from 'date-fns'
import {
  Bell,
  Filter,
  X,
  Trash2,
  Check,
  CheckCheck,
  AlertCircle,
  RefreshCw,
  Info,
  AlertTriangle,
  XCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Archive,
  Loader2,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Icon helpers (Lucide-based, replacing the text-character approach in v1.2)
// ---------------------------------------------------------------------------

const typeIcon: Record<string, typeof Info> = {
  [NotificationType.INFO]: Info,
  [NotificationType.WARNING]: AlertTriangle,
  [NotificationType.ERROR]: XCircle,
  [NotificationType.SUCCESS]: CheckCircle2,
}

const typeBadgeVariant: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  [NotificationType.INFO]: 'default',
  [NotificationType.WARNING]: 'secondary',
  [NotificationType.ERROR]: 'destructive',
  [NotificationType.SUCCESS]: 'outline',
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function NotificationsSkeleton() {
  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-9 w-56" />
          <Skeleton className="h-4 w-80 mt-2" />
        </div>
        <Skeleton className="h-10 w-40" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-16" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-12" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* List */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-start gap-4 p-4 border rounded-lg">
              <Skeleton className="h-10 w-10 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-24" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-8 w-8 rounded" />
                <Skeleton className="h-8 w-8 rounded" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function NotificationsErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="p-8">
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <AlertCircle className="h-12 w-12 text-destructive mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            Failed to Load Notifications
          </h3>
          <p className="text-muted-foreground text-center mb-6 max-w-md">
            Unable to retrieve your notifications. This may be a temporary
            network issue.
          </p>
          <Button variant="outline" onClick={onRetry}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function NotificationsPage() {
  const navigate = useNavigate()

  // -- Filters & pagination ---------------------------------------------------

  const [filterType, setFilterType] = useState<string>('all')
  const [filterRead, setFilterRead] = useState<string>('all')
  const [limit] = useState(100)
  const [offset, setOffset] = useState(0)
  const [deleteTarget, setDeleteTarget] = useState<Notification | null>(null)

  // -- API queries ------------------------------------------------------------

  const {
    data: notifications,
    isLoading,
    isError,
    refetch,
  } = useNotifications({
    unread_only: filterRead === 'unread',
    notification_type:
      filterType !== 'all' ? (filterType as NotificationType) : undefined,
    limit,
    offset,
  })

  const updateNotificationMut = useUpdateNotification()
  const deleteNotificationMut = useDeleteNotification()
  const markAllReadMut = useMarkAllRead()

  // -- Derived ----------------------------------------------------------------

  const safeNotifications = useMemo(
    () => (Array.isArray(notifications) ? notifications : []),
    [notifications]
  )

  const filteredNotifications = useMemo(() => {
    let list = safeNotifications
    // Type filter is already sent to API, but double-filter client-side for
    // immediate UI consistency when switching filters before refetch completes
    if (filterType !== 'all') {
      list = list.filter((n) => n.notification_type === filterType)
    }
    if (filterRead === 'read') {
      list = list.filter((n) => n.is_read)
    }
    if (filterRead === 'unread') {
      list = list.filter((n) => !n.is_read)
    }
    return list
  }, [safeNotifications, filterType, filterRead])

  const totalCount = safeNotifications.length
  const unreadCount = safeNotifications.filter((n) => !n.is_read).length
  const readCount = safeNotifications.filter((n) => n.is_read).length
  const todayCount = safeNotifications.filter((n) => {
    const d = new Date(n.created_at)
    const now = new Date()
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    )
  }).length

  const activeFiltersCount = [
    filterType !== 'all',
    filterRead !== 'all',
  ].filter(Boolean).length

  // -- Handlers ---------------------------------------------------------------

  const handleClearFilters = () => {
    setFilterType('all')
    setFilterRead('all')
    setOffset(0)
  }

  const handleMarkAsRead = async (notification: Notification) => {
    try {
      await updateNotificationMut.mutateAsync({
        notification_id: notification.id,
        update: { is_read: !notification.is_read },
      })
      toast.success(notification.is_read ? 'Marked as unread' : 'Marked as read')
    } catch {
      toast.error('Failed to update notification')
    }
  }

  const handleArchive = async (notification: Notification) => {
    try {
      await updateNotificationMut.mutateAsync({
        notification_id: notification.id,
        update: { is_archived: true },
      })
      toast.success('Notification archived')
    } catch {
      toast.error('Failed to archive notification')
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await markAllReadMut.mutateAsync()
      toast.success('All notifications marked as read')
    } catch {
      toast.error('Failed to mark all as read')
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteNotificationMut.mutateAsync(deleteTarget.id)
      toast.success('Notification deleted')
      setDeleteTarget(null)
    } catch {
      toast.error('Failed to delete notification')
    }
  }

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      updateNotificationMut.mutate({
        notification_id: notification.id,
        update: { is_read: true },
      })
    }
    if (notification.link) {
      navigate(notification.link)
    }
  }

  // -- Render -----------------------------------------------------------------

  if (isLoading) return <NotificationsSkeleton />
  if (isError) return <NotificationsErrorState onRetry={() => refetch()} />

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Bell className="h-8 w-8" />
            Notifications
          </h1>
          <p className="text-muted-foreground mt-1">
            Stay updated with the latest activity and alerts
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            onClick={handleMarkAllRead}
            disabled={markAllReadMut.isPending}
          >
            {markAllReadMut.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCheck className="mr-2 h-4 w-4" />
            )}
            Mark All as Read
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Unread
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {unreadCount}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Read
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">{readCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {todayCount}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters + List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              All Notifications
              {activeFiltersCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {activeFiltersCount} filter{activeFiltersCount > 1 && 's'}
                </Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-3">
              <Select value={filterType} onValueChange={(v) => { setFilterType(v); setOffset(0) }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value={NotificationType.INFO}>Info</SelectItem>
                  <SelectItem value={NotificationType.SUCCESS}>
                    Success
                  </SelectItem>
                  <SelectItem value={NotificationType.WARNING}>
                    Warning
                  </SelectItem>
                  <SelectItem value={NotificationType.ERROR}>Error</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterRead} onValueChange={(v) => { setFilterRead(v); setOffset(0) }}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="unread">Unread</SelectItem>
                  <SelectItem value="read">Read</SelectItem>
                </SelectContent>
              </Select>

              {activeFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                >
                  <X className="mr-1 h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredNotifications.length > 0 ? (
            <div className="space-y-2">
              {filteredNotifications.map((notification) => {
                const TypeIcon =
                  typeIcon[notification.notification_type] || Info

                return (
                  <div
                    key={notification.id}
                    className={cn(
                      'flex items-start gap-4 p-4 rounded-lg border transition-colors',
                      notification.is_read
                        ? 'bg-background hover:bg-muted/50'
                        : 'bg-blue-50 border-blue-200 hover:bg-blue-100 dark:bg-blue-950/20 dark:border-blue-800',
                      notification.link && 'cursor-pointer'
                    )}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    {/* Type icon */}
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white"
                      style={{
                        backgroundColor: getNotificationTypeColor(
                          notification.notification_type as NotificationType
                        ),
                      }}
                    >
                      <TypeIcon className="h-5 w-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3
                            className={cn(
                              'font-semibold text-sm',
                              !notification.is_read && 'text-blue-900 dark:text-blue-100'
                            )}
                          >
                            {notification.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(
                                new Date(notification.created_at),
                                { addSuffix: true }
                              )}
                            </span>
                            <Badge
                              variant={
                                typeBadgeVariant[
                                  notification.notification_type
                                ] || 'secondary'
                              }
                              className="text-xs"
                            >
                              {notification.notification_type}
                            </Badge>
                            {notification.link && (
                              <Badge variant="outline" className="text-xs">
                                Has Link
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div
                          className="flex items-center gap-1 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleMarkAsRead(notification)}
                            disabled={updateNotificationMut.isPending}
                            title={
                              notification.is_read
                                ? 'Mark as unread'
                                : 'Mark as read'
                            }
                          >
                            <Check
                              className={cn(
                                'h-4 w-4',
                                notification.is_read && 'text-blue-600'
                              )}
                            />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleArchive(notification)}
                            disabled={updateNotificationMut.isPending}
                            title="Archive"
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteTarget(notification)}
                            title="Delete notification"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : activeFiltersCount > 0 ? (
            <div className="text-center py-12">
              <Filter className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                No notifications match your filters
              </p>
              <Button variant="outline" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="text-center py-12">
              <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium mb-1">All caught up</p>
              <p className="text-muted-foreground">
                You don't have any notifications yet
              </p>
            </div>
          )}

          {/* Pagination */}
          {filteredNotifications.length > 0 && (
            <div className="flex items-center justify-between pt-4 mt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {offset + 1} –{' '}
                {offset + filteredNotifications.length} notifications
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset((p) => Math.max(0, p - limit))}
                  disabled={offset === 0}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset((p) => p + limit)}
                  disabled={filteredNotifications.length < limit}
                >
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirmation Dialog                                          */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Notification</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the notification{' '}
              <span className="font-medium text-foreground">
                "{deleteTarget?.title}"
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteNotificationMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteNotificationMut.isPending}
            >
              {deleteNotificationMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
